import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Paperclip, Users } from "lucide-react";
import CustomDropdown from "../../components/common/CustomDropdown";
import Avatar from "../../components/common/Avatar";
import ImageOrInitials from "../../components/agency/ImageOrInitials";
import FlagImg from "../../components/agent-tender/FlagImg";
import { usePermission } from "../../hooks/usePermission";
import { PERMISSIONS } from "../../config/permissions";
import AgentPickerRow from "../../components/club-network/AgentPickerRow";
import InfoTip from "../../components/club-network/InfoTip";
import PublicationNotice from "../../components/club-network/PublicationNotice";
import { MagnifierGlyph, CheckGlyph } from "../../components/club-network/v2/cnGlyphs";
import { useDebounce } from "../../hooks/useDebounce";
import { trustedAgentService } from "../../services/clubNetworkApi";
import {
  getPositionsForCategory,
  getTypeChoicesForCategory,
  LEAGUE_TIER_FILTER_CHOICES,
  MARKET_VALUE_CHOICES,
  FOOT_CHOICES,
  CURRENCY_CHOICES,
  AGENT_SUBJECT_CHOICES,
  AGENT_LEVEL_CHOICES,
  normalizeAgentLevel,
} from "../../constants/agentTenderChoices";

// N3 staff-role choices — mirror the backend AgentSearchRequest.staff_role enum
// (users.Staff.STAFF_TYPE_CHOICES). No free text (DoD 30): the role must be one
// of these; the label is what the club and the agent see.
const STAFF_ROLE_CHOICES = [
  { value: "coach", label: "Coach" },
  { value: "ceo", label: "CEO" },
  { value: "technical_director", label: "Technical Director" },
  { value: "sporting_director", label: "Sporting Director" },
  { value: "club_admin", label: "Club Admin" },
  { value: "head_of_scouting", label: "Head of Scouting" },
  { value: "team_manager", label: "Team Manager" },
  { value: "scout", label: "Scout" },
  { value: "medical", label: "Medical" },
  { value: "analysis", label: "Analysis" },
  { value: "operations", label: "Operations" },
  { value: "academy", label: "Academy" },
  { value: "admin", label: "Admin" },
  { value: "other", label: "Other" },
];

// Two-axis model (Part B): SUBJECT (Player/Staff → tender_category) and LEVEL
// (Professional/Youth → recruiting_for) are two independent choices. Level is
// REQUIRED for both subjects (a staff hire carries its level too) and is a
// different concept from the club's league tier (`level_expectation` below).

// N3 Create — Redesign 2.0 boards N3-5a (step 1, terms ON), N3-5a2 (terms OFF —
// the default), N3-5b (step 2), N3-5c (send confirmation) and mobile M5/M6.
// PRESENTATION ONLY: field names + choice values are the FINAL backend contract
// (agent_search_serializers): _SPEC_FIELDS + _EXTENDED_SPEC_FIELDS
// (market_value_*, foot, minimum_height, currency, maximum_transfer_fee,
// only_free_players) + recipient_agent_ids + optional multipart `document`.
// All money/range/date inputs stay FREE ENTRY (HTML hints only) per Rule 10.
const WINDOW_CHOICES = [
  { value: "Summer 2026", label: "Summer 2026" },
  { value: "Winter 2026/27", label: "Winter 2026/27" },
  { value: "Summer 2027", label: "Summer 2027" },
];
const SALARY_PERIOD_CHOICES = [
  { value: "per_year", label: "per year" },
  { value: "per_month", label: "per month" },
  { value: "per_week", label: "per week" },
];
const COMMISSION_BASIS_CHOICES = [
  { value: "percentage_of_salary", label: "% of player salary" },
  { value: "percentage_of_transfer", label: "% of transfer fee" },
  { value: "fixed_fee", label: "fixed amount" },
];
// A10/A11 — salary and loan salary-share are quoted gross or net. Applies to the
// permanent salary AND the "share we cover" on a loan (same field, relabelled).
const SALARY_BASIS_CHOICES = [
  { value: "gross", label: "gross" },
  { value: "net", label: "net" },
];
// A12 — payout mode when several agents are invited: only the delivering agent is
// paid, but the club states up front whether that is ONE commission the agents
// divide, or each agent paid in full separately.
const PAYOUT_MODE_CHOICES = [
  {
    value: "one_total",
    label: "One commission in total",
    hint: "the invited agents divide it",
  },
  {
    value: "each_separate",
    label: "Each agent paid separately",
    hint: "every delivering agent is paid in full",
  },
];

// League tier options (1st/2nd/… division) — the club/league level the request
// targets. This is the SEPARATE `level_expectation` axis, NOT the recruiting
// Level (Professional/Youth). Kept clearly named so the two never blur together.
const LEAGUE_TIER_CHOICES = LEAGUE_TIER_FILTER_CHOICES.filter((c) => c.value);

// Dropdown caret — the artboard's ▾ (U+25BE), #9CA3AF (SPEC §2.2).
const Caret = ({ small = false }) => (
  <span className={`text-[#9CA3AF] leading-none ${small ? "text-[9px] md:text-[10px]" : "text-[10px]"}`}>
    ▾
  </span>
);

// Stepper — board N3-5a/N3-5b recipe: 24px discs, 1.5px connector; the done
// step-1 disc turns #E8F1FF/#2283FF with the text "✓" (U+2713, verbatim
// symbol), the connector turns #BFDBFE. Desktop only — mobile shows the
// "1 / 2" step pill in the M5/M6 header instead.
const Stepper = ({ step }) => (
  <div className="flex items-center gap-0">
    <span className="flex items-center gap-2">
      <span
        className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[12px] font-bold ${
          step === 1 ? "bg-[#2283FF] text-white" : "bg-[#E8F1FF] text-[#2283FF]"
        }`}
      >
        {step > 1 ? "✓" : "1"}
      </span>
      <span
        className={`text-[12.5px] font-semibold ${
          step === 1 ? "text-[#111827]" : "text-[#6B7280]"
        }`}
      >
        What we're looking for
      </span>
    </span>
    <span
      className={`flex-1 h-[1.5px] mx-3 ${step > 1 ? "bg-[#BFDBFE]" : "bg-[#E5E7EB]"}`}
    />
    <span className="flex items-center gap-2">
      <span
        className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[12px] font-bold ${
          step === 2 ? "bg-[#2283FF] text-white" : "bg-[#F3F4F6] text-[#9CA3AF]"
        }`}
      >
        2
      </span>
      <span
        className={`text-[12.5px] font-semibold ${
          step === 2 ? "text-[#111827]" : "text-[#9CA3AF]"
        }`}
      >
        Choose agents
      </span>
    </span>
  </div>
);

const Field = ({ label, optional, info, children }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-[12px] font-semibold text-[#111827] flex items-center gap-1.5">
      {label}
      {optional && <span className="font-normal text-[#9CA3AF]">optional</span>}
      {info && <InfoTip text={info} />}
    </span>
    {children}
  </div>
);

// Light section separator with a heading + optional "i" (extended spec / terms).
const SectionHead = ({ title, info }) => (
  <div className="flex items-center gap-1.5 pt-1">
    <span className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#9CA3AF]">
      {title}
    </span>
    {info && <InfoTip text={info} />}
  </div>
);

const CreateSearchRequest = () => {
  const navigate = useNavigate();
  // Creating a search request needs the same permission as club tenders
  // (DoD 26). The entry button is gated on TrustedAgentHome; this mirrors the
  // backend guard for deep links to /network/trusted-agent/new.
  const canCreate = usePermission(PERMISSIONS.CREATE_TENDERS);
  const [step, setStep] = useState(1);

  // ── Step 1 spec (backend contract field names) ─────────────────────────────
  const [spec, setSpec] = useState({
    tender_category: "player", // player | staff (DoD 30)
    recruiting_for: "professional", // professional | youth (player only, C14)
    staff_role: "",
    position_primary: "",
    position_secondary: "",
    age_min: "",
    age_max: "",
    transfer_type: "buy",
    window: "Summer 2026",
    level_expectation: "1",
    additional_details: "",
    // Extended spec (DoD 21) — all optional.
    market_value_type: "",
    market_value_from: "",
    market_value_to: "",
    fix_market_value: "",
    foot: "",
    minimum_height: "",
    currency: "EUR",
    // Transfer fee / free agent (DoD 20) — mutually exclusive, Buy only.
    maximum_transfer_fee: "",
    only_free_players: false,
    // Loan terms (A11) — Loan transfer_type only. Loan fee is a yes/no first; the
    // amount only travels when a fee is paid. "Option to buy" flips the card to
    // "Loan with option". PENDING BE: see the QCS-T6 report — no columns yet.
    loan_fee_enabled: false,
    loan_fee_amount: "",
    loan_option_to_buy: false,
  });
  const [termsOn, setTermsOn] = useState(false);
  const [terms, setTerms] = useState({
    salary_amount: "",
    salary_period: "per_year",
    salary_basis: "gross", // A10 — gross/net (PENDING BE column)
    commission_value: "",
    commission_basis: "percentage_of_salary",
    commission_payout_mode: "one_total", // A12 (PENDING BE column)
    commission_note: "",
  });
  const [document, setDocument] = useState(null); // optional File (DoD 22)
  const [docError, setDocError] = useState(null); // ≤10 MB guard (global PDF rule)

  const setSpecField = (k, v) => setSpec((p) => ({ ...p, [k]: v }));
  const setTermsField = (k, v) => setTerms((p) => ({ ...p, [k]: v }));

  const positionOptions = getPositionsForCategory("player");
  // The backend stores a SINGLE secondary position. The artboard (N3-5a)
  // renders the choice as a chip row — clicking a chip selects it, clicking the
  // selected chip clears it ("no secondary"), so the single-secondary contract
  // is unchanged. The primary position is excluded, exactly as before.
  const secondaryChipOptions = useMemo(
    () => positionOptions.filter((o) => o.value !== spec.position_primary),
    [positionOptions, spec.position_primary],
  );

  const isStaff = spec.tender_category === "staff";
  const typeChoices = getTypeChoicesForCategory(spec.tender_category);
  // A staff request has no "player" — drop the word from the commission-basis
  // salary option (the VALUE percentage_of_salary is unchanged, only the label).
  const commissionBasisChoices = useMemo(
    () =>
      COMMISSION_BASIS_CHOICES.map((c) =>
        c.value === "percentage_of_salary"
          ? { ...c, label: isStaff ? "% of salary" : "% of player salary" }
          : c,
      ),
    [isStaff],
  );
  // Fixed-fee commission is a money AMOUNT + currency, not a percentage.
  const isFixedCommission = terms.commission_basis === "fixed_fee";
  const isBuy = spec.transfer_type === "buy";
  // A11 — loan terms surface only for a player Loan (staff has no loan type).
  const isLoan = !isStaff && spec.transfer_type === "loan";
  const mvType = spec.market_value_type;
  // Player: position + age required (unchanged). Staff: only the role is required
  // (age is optional for staff, DoD 30).
  const step1Valid = isStaff
    ? !!spec.staff_role
    : spec.position_primary && spec.age_min && spec.age_max;

  // Two-axis model (Part B). SUBJECT switch resets the subject-specific fields
  // and picks a sensible default engagement type (player → buy, staff →
  // full_time); it does NOT touch the LEVEL (recruiting_for), which is an
  // independent axis required for both subjects.
  const setSubject = (value) =>
    setSpec((p) => {
      const staff = value === "staff";
      return {
        ...p,
        tender_category: staff ? "staff" : "player",
        position_primary: "",
        position_secondary: "",
        staff_role: "",
        transfer_type: staff ? "full_time" : "buy",
      };
    });

  // DoD 20 — transfer fee and "free agent" are mutually exclusive; setting one
  // disables (and clears) the other so the two states are always unambiguous.
  const setOnlyFreePlayers = (checked) =>
    setSpec((p) => ({
      ...p,
      only_free_players: checked,
      maximum_transfer_fee: checked ? "" : p.maximum_transfer_fee,
    }));
  const setTransferFee = (v) =>
    setSpec((p) => ({
      ...p,
      maximum_transfer_fee: v,
      only_free_players: v ? false : p.only_free_players,
    }));

  // ── Step 2 agents ────────────────────────────────────────────────────────
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 400);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  // A0-2 — one optional personal note that goes to ALL invited agents. Its own
  // field (BE `personal_note`, in _SPEC_FIELDS), separate from the spec free-text
  // (`additional_details`) and the terms note (`commission_note`).
  const [personalNote, setPersonalNote] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (step !== 2) return undefined;
    let cancelled = false;
    trustedAgentService.searchAgents(debouncedQuery).then((res) => {
      if (!cancelled && res.success) setResults(res.data || []);
    });
    return () => {
      cancelled = true;
    };
  }, [step, debouncedQuery]);

  const selectedIds = useMemo(() => new Set(selected.map((a) => a.id)), [selected]);
  const toggleAgent = (agent) => {
    setSelected((prev) =>
      prev.some((a) => a.id === agent.id)
        ? prev.filter((a) => a.id !== agent.id)
        : [...prev, agent],
    );
  };

  // Board N3-5b groups results BY AGENCY (group header: 20px mini-crest +
  // agency name + flag country · N agents). Pure display grouping over the same
  // normalized search results — order of first appearance is kept.
  const groupedResults = useMemo(() => {
    const map = new Map();
    results.forEach((a) => {
      const key = a.agency_name || "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    });
    return Array.from(map.entries());
  }, [results]);

  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  const handleSend = async () => {
    setSending(true);
    setError(null);
    // Currency travels when any money field uses it: market value / transfer fee
    // (player only) OR a fixed-fee commission (player or staff). One currency per
    // request — the same `currency` field the transfer fee already uses.
    const usesCurrency =
      (!isStaff &&
        (mvType === "market_value_range" ||
          mvType === "market_value_fixed" ||
          (isBuy && spec.maximum_transfer_fee) ||
          (isLoan && spec.loan_fee_enabled && spec.loan_fee_amount))) ||
      (termsOn && terms.commission_basis === "fixed_fee" && terms.commission_value);
    const payload = {
      // Category (DoD 30) — staff sends the role and omits position; player is
      // unchanged. The backend enforces the same rule.
      tender_category: spec.tender_category,
      // Level axis (Part B): recruiting_for [professional|youth] is required for
      // BOTH subjects and is sent for staff too — never cleared.
      recruiting_for: normalizeAgentLevel(spec.recruiting_for),
      staff_role: isStaff ? spec.staff_role : null,
      position_primary: isStaff ? null : spec.position_primary,
      position_secondary: isStaff ? null : spec.position_secondary || null,
      age_min: num(spec.age_min),
      age_max: num(spec.age_max),
      transfer_type: spec.transfer_type,
      window: spec.window,
      level_expectation: spec.level_expectation,
      additional_details: spec.additional_details,
      // Extended spec (DoD 21) — player attributes only; staff sends none of
      // these (fields are hidden for staff). Only filled fields travel.
      market_value_type: isStaff ? null : mvType || null,
      market_value_from:
        !isStaff && mvType === "market_value_range" ? num(spec.market_value_from) : null,
      market_value_to:
        !isStaff && mvType === "market_value_range" ? num(spec.market_value_to) : null,
      fix_market_value:
        !isStaff && mvType === "market_value_fixed" ? num(spec.fix_market_value) : null,
      foot: isStaff ? null : spec.foot || null,
      minimum_height: isStaff ? null : num(spec.minimum_height),
      currency: usesCurrency ? spec.currency : null,
      // Transfer fee / free agent (DoD 20) — Buy only, mutually exclusive.
      maximum_transfer_fee:
        !isStaff && isBuy && !spec.only_free_players ? num(spec.maximum_transfer_fee) : null,
      only_free_players: !isStaff && isBuy ? !!spec.only_free_players : false,
      // Loan terms (A11) — player Loan only. loan_fee_amount only when a fee is
      // paid. NOTE (QCS-T6 report): the backend model/serializer carry NO columns
      // for these yet — they need an additive migration before they persist.
      loan_fee_enabled: isLoan ? !!spec.loan_fee_enabled : false,
      loan_fee_amount:
        isLoan && spec.loan_fee_enabled ? num(spec.loan_fee_amount) : null,
      loan_option_to_buy: isLoan ? !!spec.loan_option_to_buy : false,
      // Terms (DoD 19) — salary and commission are independent; either, both, or
      // neither is valid. Send only when the toggle is on.
      terms_enabled: termsOn,
      salary_amount: termsOn ? num(terms.salary_amount) : null,
      salary_period: termsOn ? terms.salary_period : null,
      // salary_basis (A10 gross/net) + commission_payout_mode (A12) — PENDING BE
      // columns (see report); sent now so they persist the moment BE adds them.
      salary_basis: termsOn ? terms.salary_basis : null,
      commission_value: termsOn ? num(terms.commission_value) : null,
      commission_basis: termsOn ? terms.commission_basis : null,
      commission_payout_mode: termsOn ? terms.commission_payout_mode : null,
      commission_note: termsOn ? terms.commission_note : null,
      // A0-2 personal note to the agents (one note, goes to all) — separate BE
      // field from additional_details / commission_note. Trimmed; null when blank.
      personal_note: personalNote.trim() || null,
      recipient_agent_ids: selected.map((a) => a.id),
      document: document || undefined, // multipart when present (DoD 22)
    };
    const res = await trustedAgentService.createRequest(payload);
    setSending(false);
    if (res.success) setSent(true);
    else setError(res.error || "Could not send the request. Please try again.");
  };

  // Input recipe — desktop N3-5a: #F3F4F6 fill, radius 8, 9×12 padding, 13px.
  // Mobile M5: white fill, radius 10, 11×13 padding, 13.5px.
  const inputCls =
    "w-full bg-white md:bg-[#F3F4F6] border border-[#E5E7EB] rounded-[10px] md:rounded-lg px-[13px] py-[11px] md:px-3 md:py-[9px] text-[13.5px] md:text-[13px] text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#2283FF]";
  // Terms sub-fields keep the #F3F4F6 fill on mobile too (M5 recipe).
  const termsInputCls =
    "w-full bg-[#F3F4F6] border border-[#E5E7EB] rounded-[10px] md:rounded-lg px-3 py-2.5 md:py-[9px] text-[13px] text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#2283FF]";

  if (!canCreate) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-white border border-[#D6DBE3] rounded-xl p-7 flex flex-col items-center text-center gap-2">
          <span className="text-[12.5px] text-gray-500">
            You don't have permission to create search requests. Ask your club admin.
          </span>
          <button
            onClick={() => navigate("/network/trusted-agent")}
            className="mt-2 px-4 py-2 border border-gray-200 rounded-lg text-[12.5px] font-semibold hover:bg-gray-50"
          >
            Back to my search requests
          </button>
        </div>
      </div>
    );
  }

  if (sent) {
    // Board N3-5c — send confirmation: 44px #ECFDF5 disc + verbatim S4 check
    // 18×14 green, title 14.5/700, copy 12.5 lh1.55, quiet secondary CTA. The
    // artboard names the recipients ("Davor, Stefan and Élise will accept or
    // decline.") — built from the actual selection, sample names never ship.
    const nameList = selected.length
      ? selected
          .map((a) => a.name)
          .reduce(
            (acc, n, i, arr) =>
              i === 0 ? n : i === arr.length - 1 ? `${acc} and ${n}` : `${acc}, ${n}`,
            "",
          )
      : "They";
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-white border border-[#D6DBE3] rounded-xl p-7 flex flex-col items-center text-center gap-[9px]">
          <span className="w-11 h-11 rounded-full bg-[#ECFDF5] flex items-center justify-center">
            <CheckGlyph width={18} height={14} stroke="#16A34A" />
          </span>
          <span className="text-[14.5px] font-bold text-[#111827]">
            Request sent to {selected.length} agent{selected.length === 1 ? "" : "s"}
          </span>
          <span className="text-[12.5px] text-[#6B7280] leading-[1.55]">
            {nameList} will accept or decline. When one accepts, a chat opens.
          </span>
          <button
            onClick={() => navigate("/network/trusted-agent")}
            className="mt-0.5 px-3.5 py-2 bg-white border border-[#E5E7EB] rounded-lg text-[12.5px] font-semibold text-[#111827] hover:bg-[#F3F4F6]"
          >
            Back to my search requests
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 md:py-6 pb-24 md:pb-6">
        {/* Mobile header — boards M5/M6: ‹ back + title + "1 / 2" step pill.
            The ‹ maps to the SAME handlers as the desktop footer: step 1 →
            Cancel navigation, step 2 → Back to step 1. */}
        <div className="md:hidden flex items-center gap-2.5 mb-3">
          <button
            type="button"
            aria-label="Back"
            onClick={() => (step === 1 ? navigate("/network/trusted-agent") : setStep(1))}
            className="text-[20px] text-[#6B7280] leading-none"
          >
            ‹
          </button>
          <span className="text-[15px] font-bold text-[#111827] flex-1">
            {step === 1 ? "New search request" : "Choose agents"}
          </span>
          <span className="text-[12px] font-semibold text-[#6B7280] bg-[#F3F4F6] rounded-full px-[9px] py-[3px]">
            {step} / 2
          </span>
        </div>

        <div className="md:bg-white md:border md:border-[#D6DBE3] md:rounded-xl">
          {/* Desktop header — board N3-5a: title 17/700 + stepper. */}
          <div className="hidden md:flex flex-col gap-4 px-7 pt-[22px]">
            <h1 className="text-[17px] font-bold text-[#111827]">New search request</h1>
            <Stepper step={step} />
          </div>

          {step === 1 ? (
            <div className="md:px-7 md:py-5 pt-1 flex flex-col gap-4">
              {/* Publication clarity (DoD 23) — what the club is about to do. */}
              <PublicationNotice variant="n3-create" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-x-[18px] md:gap-y-3.5">
                <Field
                  label="Subject"
                  info="Search for a player (keys on a position) or for staff/trainer (coach, sporting director, scout…)."
                >
                  <CustomDropdown
                    options={AGENT_SUBJECT_CHOICES}
                    value={spec.tender_category}
                    onChange={setSubject}
                    displayProperty="label"
                    valueProperty="value"
                    paddingY=""
                    className={inputCls}
                    chevronIcon={<Caret />}
                  />
                </Field>
                {/* D-MARIN-1 (9): label pair "Recruiting level" / "League
                    expectation" resolves the Level-label collision. The
                    Professional/Youth axis itself (choices, recruiting_for
                    state, every other surface) is untouched. */}
                <Field
                  label="Recruiting level"
                  info="Professional or Youth: the recruiting level of the request. Applies to staff too (e.g. a youth academy coach). This is not the club's league tier."
                >
                  <CustomDropdown
                    options={AGENT_LEVEL_CHOICES}
                    value={normalizeAgentLevel(spec.recruiting_for)}
                    onChange={(v) => setSpecField("recruiting_for", v)}
                    displayProperty="label"
                    valueProperty="value"
                    paddingY=""
                    className={inputCls}
                    chevronIcon={<Caret />}
                  />
                </Field>
                {isStaff ? (
                  <Field label="Staff role">
                    <CustomDropdown
                      options={STAFF_ROLE_CHOICES}
                      value={spec.staff_role}
                      onChange={(v) => setSpecField("staff_role", v)}
                      placeholder="Select a role"
                      displayProperty="label"
                      valueProperty="value"
                    paddingY=""
                      searchEnabled
                      className={inputCls}
                      chevronIcon={<Caret />}
                    />
                  </Field>
                ) : (
                  <Field label="Position">
                    <CustomDropdown
                      options={positionOptions}
                      value={spec.position_primary}
                      onChange={(v) => setSpecField("position_primary", v)}
                      placeholder="Select a position"
                      displayProperty="label"
                      valueProperty="value"
                    paddingY=""
                      searchEnabled
                      className={inputCls}
                      chevronIcon={<Caret />}
                    />
                  </Field>
                )}
                {!isStaff && (
                  // Board N3-5a renders the secondary choice as a chip row
                  // (selected chip carries the verbatim "✓"). Same single-
                  // secondary state as the old dropdown: tap to select, tap the
                  // selected chip to clear.
                  <Field label="Secondary positions" optional>
                    <div className="flex gap-1.5 flex-wrap pt-0.5">
                      {secondaryChipOptions.map((o) => {
                        const sel = spec.position_secondary === o.value;
                        return (
                          <button
                            key={o.value}
                            type="button"
                            onClick={() =>
                              setSpecField("position_secondary", sel ? "" : o.value)
                            }
                            className={`text-[12px] font-semibold px-3 py-[5px] rounded-full border ${
                              sel
                                ? "bg-[#E8F1FF] text-[#2283FF] border-[#BFDBFE]"
                                : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#D1D5DB]"
                            }`}
                          >
                            {o.label}
                            {sel ? " ✓" : ""}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                )}
                <Field label="Age range" optional={isStaff}>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={spec.age_min}
                      onChange={(e) => setSpecField("age_min", e.target.value)}
                      placeholder="18"
                      min="14"
                      max="45"
                      className={inputCls}
                    />
                    <span className="text-[#9CA3AF] text-[12px]">to</span>
                    <input
                      type="number"
                      value={spec.age_max}
                      onChange={(e) => setSpecField("age_max", e.target.value)}
                      placeholder="24"
                      min="14"
                      max="45"
                      className={inputCls}
                    />
                  </div>
                </Field>
                <Field label={isStaff ? "Engagement type" : "Transfer type"}>
                  <CustomDropdown
                    options={typeChoices}
                    value={spec.transfer_type}
                    onChange={(v) => setSpecField("transfer_type", v)}
                    displayProperty="label"
                    valueProperty="value"
                    paddingY=""
                    className={inputCls}
                    chevronIcon={<Caret />}
                  />
                </Field>
                <Field label="Transfer window">
                  <CustomDropdown
                    options={WINDOW_CHOICES}
                    value={spec.window}
                    onChange={(v) => setSpecField("window", v)}
                    displayProperty="label"
                    valueProperty="value"
                    paddingY=""
                    className={inputCls}
                    chevronIcon={<Caret />}
                  />
                </Field>
                <Field
                  label="League expectation"
                  info="The club/league level the request targets (1st, 2nd, … division). Separate from the Professional/Youth recruiting Level above."
                >
                  <CustomDropdown
                    options={LEAGUE_TIER_CHOICES}
                    value={spec.level_expectation}
                    onChange={(v) => setSpecField("level_expectation", v)}
                    displayProperty="label"
                    valueProperty="value"
                    paddingY=""
                    className={inputCls}
                    chevronIcon={<Caret />}
                  />
                </Field>
                {!isStaff && (
                  <Field
                    label="Preferred foot"
                    optional
                    info="The player's stronger foot. Leave empty if it doesn't matter."
                  >
                    <CustomDropdown
                      options={[{ value: "", label: "No preference" }, ...FOOT_CHOICES]}
                      value={spec.foot}
                      onChange={(v) => setSpecField("foot", v)}
                      placeholder="No preference"
                      displayProperty="label"
                      valueProperty="value"
                    paddingY=""
                      className={inputCls}
                      chevronIcon={<Caret />}
                    />
                  </Field>
                )}
                {!isStaff && (
                  <Field
                    label="Minimum height"
                    optional
                    info="Smallest acceptable height in centimetres. Leave empty for no minimum."
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={spec.minimum_height}
                        onChange={(e) => setSpecField("minimum_height", e.target.value)}
                        placeholder="180"
                        min="140"
                        max="220"
                        className={inputCls}
                      />
                      <span className="text-[#9CA3AF] text-[12px] flex-none">cm</span>
                    </div>
                  </Field>
                )}
              </div>

              {/* Budget & value (DoD 20/21) — player attributes; hidden for staff
                  (market value / transfer fee don't apply to a staff hire). Salary
                  and commission still live in the Terms block below. */}
              {!isStaff && (
              <>
              <SectionHead
                title="Budget & player value"
                info="Optional guidance on value and cost. Only the fields you fill are sent."
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-x-[18px] md:gap-y-3.5">
                <Field label="Currency" optional>
                  <CustomDropdown
                    options={CURRENCY_CHOICES}
                    value={spec.currency}
                    onChange={(v) => setSpecField("currency", v)}
                    displayProperty="label"
                    valueProperty="value"
                    paddingY=""
                    className={inputCls}
                    chevronIcon={<Caret />}
                  />
                </Field>
                <Field
                  label="Market value"
                  optional
                  info="Target market value of the player: a range, a fixed figure, or not important."
                >
                  <CustomDropdown
                    options={[
                      { value: "", label: "Not specified" },
                      ...MARKET_VALUE_CHOICES,
                    ]}
                    value={mvType}
                    onChange={(v) => setSpecField("market_value_type", v)}
                    placeholder="Not specified"
                    displayProperty="label"
                    valueProperty="value"
                    paddingY=""
                    className={inputCls}
                    chevronIcon={<Caret />}
                  />
                </Field>
                {mvType === "market_value_range" && (
                  <div className="md:col-span-2">
                    <Field label="Market value range">
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={spec.market_value_from}
                          onChange={(e) => setSpecField("market_value_from", e.target.value)}
                          placeholder="from"
                          className={inputCls}
                        />
                        <span className="text-[#9CA3AF] text-[12px]">to</span>
                        <input
                          type="number"
                          value={spec.market_value_to}
                          onChange={(e) => setSpecField("market_value_to", e.target.value)}
                          placeholder="to"
                          className={inputCls}
                        />
                      </div>
                    </Field>
                  </div>
                )}
                {mvType === "market_value_fixed" && (
                  <div className="md:col-span-2">
                    <Field label="Fixed market value">
                      <input
                        type="number"
                        value={spec.fix_market_value}
                        onChange={(e) => setSpecField("fix_market_value", e.target.value)}
                        placeholder="e.g. 500000"
                        className={inputCls}
                      />
                    </Field>
                  </div>
                )}

                {isBuy && (
                  <div className="md:col-span-2 flex flex-col gap-2.5 bg-[#F8FAFC] border border-[#E5E7EB] rounded-xl p-3.5">
                    <Field
                      label="Maximum transfer fee"
                      optional
                      info="The most you'd pay in a transfer fee. Leave empty if open, or mark free agents only below."
                    >
                      <input
                        type="number"
                        value={spec.maximum_transfer_fee}
                        onChange={(e) => setTransferFee(e.target.value)}
                        placeholder="e.g. 1000000"
                        disabled={spec.only_free_players}
                        className={`${inputCls} disabled:opacity-50 disabled:cursor-not-allowed`}
                      />
                    </Field>
                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={spec.only_free_players}
                        onChange={(e) => setOnlyFreePlayers(e.target.checked)}
                        disabled={!!spec.maximum_transfer_fee}
                        className="w-4 h-4 rounded border-gray-300 text-[#2283FF] focus:ring-[#2283FF] disabled:opacity-50"
                      />
                      <span className="text-[12.5px] font-medium text-[#111827]">
                        Free agents only (no transfer fee)
                      </span>
                      <InfoTip text="Search only for out-of-contract players. Cannot be combined with a transfer fee." />
                    </label>
                  </div>
                )}

                {/* Loan terms (A11) — Loan transfer_type only, mirrors the
                    transfer-fee card. Loan fee is a yes/no first; the amount
                    appears only when a fee is paid. "Option to buy" → the card
                    shows "Loan with option". Mobile: the segmented pair and the
                    checkbox stack (flex-col). */}
                {isLoan && (
                  <div className="md:col-span-2 flex flex-col gap-2.5 bg-[#F8FAFC] border border-[#E5E7EB] rounded-xl p-3.5">
                    <Field
                      label="Loan fee"
                      info="Do you pay the lending club a loan fee for the season? The amount is only asked if you do."
                    >
                      <div className="flex flex-col sm:flex-row gap-2">
                        {[
                          { v: false, l: "No loan fee" },
                          { v: true, l: "We pay a loan fee" },
                        ].map((o) => {
                          const sel = !!spec.loan_fee_enabled === o.v;
                          return (
                            <button
                              key={String(o.v)}
                              type="button"
                              onClick={() =>
                                setSpec((p) => ({
                                  ...p,
                                  loan_fee_enabled: o.v,
                                  loan_fee_amount: o.v ? p.loan_fee_amount : "",
                                }))
                              }
                              className={`flex-1 text-left rounded-[10px] border px-3 py-2.5 text-[12.5px] font-semibold ${
                                sel
                                  ? "bg-[#E8F1FF] text-[#1B6FE0] border-[#BFDBFE]"
                                  : "bg-white text-[#111827] border-[#E5E7EB] hover:border-[#D1D5DB]"
                              }`}
                            >
                              {o.l}
                            </button>
                          );
                        })}
                      </div>
                    </Field>
                    {spec.loan_fee_enabled && (
                      <Field label="Loan fee amount" optional info="For the season.">
                        <input
                          type="number"
                          value={spec.loan_fee_amount}
                          onChange={(e) => setSpecField("loan_fee_amount", e.target.value)}
                          placeholder="e.g. 500000"
                          className={inputCls}
                        />
                      </Field>
                    )}
                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={spec.loan_option_to_buy}
                        onChange={(e) => setSpecField("loan_option_to_buy", e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-[#2283FF] focus:ring-[#2283FF]"
                      />
                      <span className="text-[12.5px] font-medium text-[#111827]">
                        Option to buy wanted
                      </span>
                      <InfoTip text="You'd want a clause to buy the player later. The anonymous card then shows 'Loan with option'." />
                    </label>
                  </div>
                )}
              </div>
              </>
              )}

              <Field label="Additional details" optional>
                <textarea
                  rows={2}
                  value={spec.additional_details}
                  onChange={(e) => setSpecField("additional_details", e.target.value)}
                  placeholder={
                    isStaff
                      ? "e.g. UEFA Pro licence, experience in top-tier youth development"
                      : "e.g. left-footed preferred, pace over physique"
                  }
                  className={inputCls}
                />
              </Field>

              {/* Optional attachment (DoD 22). */}
              <Field
                label="Attach a document"
                optional
                info="Optionally share a scouting brief or profile (PDF, image, doc). Only the agents you pick can open it."
              >
                {document ? (
                  <div className="flex items-center gap-3 bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg px-3 py-2.5">
                    <Paperclip className="w-4 h-4 text-[#2283FF] flex-none" />
                    <span className="text-[12.5px] text-[#111827] truncate flex-1">
                      {document.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setDocument(null);
                        setDocError(null);
                      }}
                      className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center hover:bg-[#FEE2E2] hover:text-[#DC2626]"
                      aria-label="Remove document"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2.5 border border-dashed border-[#D1D5DB] rounded-lg px-3 py-2.5 cursor-pointer hover:border-[#2283FF] hover:bg-[#F8FAFC] bg-white md:bg-transparent">
                    <span className="w-6 h-6 rounded-full bg-[#E8F1FF] text-[#2283FF] flex items-center justify-center flex-none">
                      <Paperclip className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-[12.5px] font-semibold text-[#2283FF]">
                      Choose a file
                    </span>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,image/*"
                      className="hidden"
                      // Global PDF rule: one file per request, ≤10 MB. Oversize is
                      // rejected here before it ever reaches the multipart upload.
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        if (f && f.size > 10 * 1024 * 1024) {
                          setDocError(
                            "That file is over 10 MB. Please attach one file up to 10 MB.",
                          );
                          e.target.value = "";
                          return;
                        }
                        setDocError(null);
                        setDocument(f);
                      }}
                    />
                  </label>
                )}
                {docError ? (
                  <span className="text-[11.5px] text-[#DC2626]">{docError}</span>
                ) : (
                  <span className="text-[11px] text-[#9CA3AF]">One file, up to 10 MB.</span>
                )}
              </Field>

              {/* Terms — boards N3-5a (ON) / N3-5a2 (OFF — the default: one
                  quiet row). Salary and commission are independent (DoD 19).
                  NOTE: no `overflow-hidden` on this wrapper — it would clip the
                  commission-basis dropdown menu that opens downward. Corners are
                  rounded on the toggle ROW itself so the visual stays clean
                  without clipping. */}
              <div className="border border-[#E5E7EB] rounded-[14px] md:rounded-xl bg-white md:bg-transparent">
                {/* The info affordance is a SIBLING of the toggle, not a child
                    of it. It used to sit between the label and the "optional"
                    hint — measured at 360 that put its centre at 50.6% of the
                    row's width, i.e. dead centre of the switch's own tap
                    target — and it carried `stopPropagation` (rightly: tapping
                    "i" must not flip the switch). The two together made the
                    middle of the row a dead zone: a tap there opened the
                    tooltip and left the switch untouched. Moving it out of the
                    button restores the whole row as one tap target, keeps the
                    tooltip a tooltip, and drops an interactive element nested
                    inside a <button> (Tooltip's trigger is a div with its own
                    onClick). The design board for this row (N3-5a / M5) lists
                    toggle + label + "optional" only, so nothing is displaced
                    that the board asked for. */}
                <div
                  className={`w-full flex items-center gap-2.5 px-4 bg-[#F8FAFC] rounded-t-[14px] md:rounded-t-xl ${
                    termsOn ? "" : "rounded-b-[14px] md:rounded-b-xl"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setTermsOn((v) => !v)}
                    aria-pressed={termsOn}
                    className="flex-1 min-w-0 flex items-center gap-2.5 py-[13px] text-left"
                  >
                    <span
                      className={`w-9 h-5 rounded-full relative flex-none transition-colors ${
                        termsOn ? "bg-[#2283FF]" : "bg-[#E5E7EB]"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-[0_1px_2px_rgba(17,24,39,0.2)] transition-all ${
                          termsOn ? "right-0.5" : "left-0.5"
                        }`}
                      />
                    </span>
                    <span className="text-[13px] font-semibold text-[#111827]">Include terms</span>
                    <span className="ml-auto pl-2 text-[10.5px] md:text-[11.5px] text-[#9CA3AF]">
                      <span className="md:hidden">optional</span>
                      <span className="hidden md:inline">
                        Optional: the request works without them
                      </span>
                    </span>
                  </button>
                  <span className="flex items-center flex-none">
                    <InfoTip text="Salary and commission are independent: offer one, both, or neither. The request works without any terms." />
                  </span>
                </div>
                {termsOn && (
                  // Board N3-5a terms fields: 2-col grid (gap 14×18), "Note on
                  // terms" spanning the full width; single column on mobile M5.
                  <div className="p-4 border-t border-[#F3F4F6] md:border-[#E5E7EB] grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-x-[18px] md:gap-y-3.5">
                    {/* A10 salary "up to" + gross/net; A11 relabels it to the
                        loan "share we cover". Row wraps on narrow columns —
                        amount holds a min width, period + basis fall to the next
                        line rather than shrink (translations spec §3). Visible to
                        the working agent only, never on the anonymous card. */}
                    <Field
                      label={isLoan ? "Salary share we cover" : "Salary offered"}
                      optional
                      info={
                        isLoan
                          ? "The most of the player's salary you'd cover during the loan. Seen only by the working agent, never on the anonymous card."
                          : "The most you'd pay. Seen only by the working agent, never on the anonymous card."
                      }
                    >
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={terms.salary_amount}
                          onChange={(e) => setTermsField("salary_amount", e.target.value)}
                          placeholder="180000"
                          className={`${termsInputCls} flex-1 min-w-0`}
                        />
                        <div className="w-[92px] flex-none">
                          <CustomDropdown
                            options={SALARY_PERIOD_CHOICES}
                            value={terms.salary_period}
                            onChange={(v) => setTermsField("salary_period", v)}
                            displayProperty="label"
                            valueProperty="value"
                    paddingY=""
                            className={termsInputCls}
                            chevronIcon={<Caret small />}
                          />
                        </div>
                        <div className="w-[72px] flex-none">
                          <CustomDropdown
                            options={SALARY_BASIS_CHOICES}
                            value={terms.salary_basis}
                            onChange={(v) => setTermsField("salary_basis", v)}
                            displayProperty="label"
                            valueProperty="value"
                    paddingY=""
                            className={termsInputCls}
                            chevronIcon={<Caret small />}
                          />
                        </div>
                      </div>
                    </Field>
                    <Field label="Agent commission" optional>
                      {/* Value input (a percentage, or a money amount for a fixed
                          fee) + optional currency select (fixed fee) + basis
                          dropdown in a flex-1 wrapper so the select fills the row
                          and opens fully (portal menu — never clipped). */}
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={terms.commission_value}
                          onChange={(e) => setTermsField("commission_value", e.target.value)}
                          placeholder={isFixedCommission ? "e.g. 5000" : "10"}
                          className={`${termsInputCls} !w-16 flex-none px-2`}
                        />
                        {isFixedCommission && (
                          <div className="w-24 flex-none">
                            <CustomDropdown
                              options={CURRENCY_CHOICES}
                              value={spec.currency}
                              onChange={(v) => setSpecField("currency", v)}
                              displayProperty="label"
                              valueProperty="value"
                    paddingY=""
                              className={termsInputCls}
                              chevronIcon={<Caret small />}
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <CustomDropdown
                            options={commissionBasisChoices}
                            value={terms.commission_basis}
                            onChange={(v) => setTermsField("commission_basis", v)}
                            displayProperty="label"
                            valueProperty="value"
                    paddingY=""
                            className={termsInputCls}
                            chevronIcon={<Caret small />}
                          />
                        </div>
                      </div>
                    </Field>
                    {/* A12 — payout mode. Two selectable rows: stack on mobile
                        (flex-col), side by side from sm. Long labels (DE +35%)
                        keep their own line, never shrink. */}
                    <div className="md:col-span-2">
                      <Field
                        label="How the commission is paid"
                        optional
                        info="Only the agent who delivers is paid. State up front whether that is one commission the invited agents divide, or each agent paid in full separately."
                      >
                        <div className="flex flex-col sm:flex-row gap-2">
                          {PAYOUT_MODE_CHOICES.map((o) => {
                            const sel = terms.commission_payout_mode === o.value;
                            return (
                              <button
                                key={o.value}
                                type="button"
                                onClick={() =>
                                  setTermsField("commission_payout_mode", o.value)
                                }
                                className={`flex-1 text-left rounded-[10px] border px-3 py-2.5 ${
                                  sel
                                    ? "bg-[#E8F1FF] border-[#BFDBFE]"
                                    : "bg-white md:bg-[#F3F4F6] border-[#E5E7EB] hover:border-[#D1D5DB]"
                                }`}
                              >
                                <span
                                  className={`block text-[12.5px] font-semibold ${
                                    sel ? "text-[#1B6FE0]" : "text-[#111827]"
                                  }`}
                                >
                                  {o.label}
                                </span>
                                <span className="block text-[11px] text-[#6B7280] mt-0.5">
                                  {o.hint}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </Field>
                    </div>
                    <div className="md:col-span-2">
                      <Field label="Note on terms" optional>
                        <input
                          type="text"
                          value={terms.commission_note}
                          onChange={(e) => setTermsField("commission_note", e.target.value)}
                          placeholder="e.g. negotiable for the right profile"
                          className={termsInputCls}
                        />
                      </Field>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="md:px-7 md:py-5 pt-1 flex flex-col gap-3.5">
              {selected.length > 0 && (
                <div className="flex items-center gap-2 md:gap-2 gap-y-1.5 flex-wrap">
                  <span className="hidden md:inline text-[11px] font-bold uppercase tracking-[0.06em] text-[#9CA3AF] flex-none">
                    Selected · {selected.length}
                  </span>
                  {selected.map((a) => (
                    <React.Fragment key={a.id}>
                      {/* Desktop chip — board N3-5b: 20px avatar + name + 15px
                          white × disc. */}
                      <span className="hidden md:inline-flex items-center gap-1.5 bg-[#E8F1FF] border border-[#BFDBFE] rounded-full py-1 pl-[5px] pr-2">
                        <Avatar src={a.avatar} fallbackText={a.name} size={20} />
                        <span className="text-[12px] font-semibold text-[#1B6FE0]">
                          {a.name}
                        </span>
                        <button
                          onClick={() => toggleAgent(a)}
                          aria-label="Remove"
                          className="w-[15px] h-[15px] rounded-full bg-white text-[#2283FF] text-[9px] leading-none flex items-center justify-center"
                        >
                          ×
                        </button>
                      </span>
                      {/* Mobile chip — board M6: text chip, plain × (10px). */}
                      <span className="md:hidden inline-flex items-center gap-[5px] bg-[#E8F1FF] border border-[#BFDBFE] rounded-full pl-3 pr-[9px] py-[5px] text-[12px] font-semibold text-[#1B6FE0]">
                        {a.name}
                        <button
                          onClick={() => toggleAgent(a)}
                          aria-label="Remove"
                          className="text-[#2283FF] text-[10px] leading-none"
                        >
                          ×
                        </button>
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              )}

              {/* Search — verbatim S3 magnifier; gray fill on the desktop card
                  (N3-5b), white on mobile (M6). Same debounced searchAgents
                  lane as before (platform agent search reuse). */}
              <div className="flex items-center gap-2 bg-white md:bg-[#F3F4F6] border border-[#E5E7EB] rounded-[10px] md:rounded-lg px-[13px] py-[11px] md:px-3 md:py-[9px]">
                <MagnifierGlyph size={14} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search agencies or agents…"
                  className="w-full bg-transparent text-[13.5px] md:text-[13px] placeholder:text-[#9CA3AF] focus:outline-none"
                />
              </div>

              <div className="flex flex-col gap-1 max-h-80 overflow-y-auto">
                {results.length === 0 ? (
                  <p className="text-[12.5px] text-gray-400 py-4 text-center">
                    No agents match your search.
                  </p>
                ) : (
                  <>
                    {/* Desktop — board N3-5b: results grouped by agency with a
                        group header (20px mini-crest + name + flag country ·
                        N agents). */}
                    <div className="hidden md:flex flex-col gap-1">
                      {groupedResults.map(([agencyName, agents]) => (
                        <div key={agencyName || "no-agency"} className="flex flex-col gap-1">
                          {agencyName && (
                            <div className="flex items-center gap-2 px-3 pt-2 pb-1">
                              {agents[0].agency_logo ? (
                                <img
                                  src={agents[0].agency_logo}
                                  alt={agencyName}
                                  className="w-5 h-5 rounded-[5px] object-contain border border-[#E5E7EB] bg-[#F3F4F6] flex-none"
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                <span className="flex-none">
                                  <ImageOrInitials src={null} alt={agencyName} size="small" />
                                </span>
                              )}
                              <span className="text-[12px] font-bold text-[#111827]">
                                {agencyName}
                              </span>
                              <span className="inline-flex items-center gap-[5px] text-[11.5px] text-[#6B7280]">
                                {agents[0].agency_country && (
                                  <>
                                    <FlagImg
                                      country={agents[0].agency_country}
                                      className="w-[15px] h-[11px] rounded-[2px]"
                                    />
                                    {agents[0].agency_country} ·{" "}
                                  </>
                                )}
                                {agents.length} agent{agents.length === 1 ? "" : "s"}
                              </span>
                            </div>
                          )}
                          {agents.map((a) => (
                            <AgentPickerRow
                              key={a.id}
                              agent={a}
                              selected={selectedIds.has(a.id)}
                              onToggle={toggleAgent}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                    {/* Mobile — board M6: flat rows, no group headers. */}
                    <div className="md:hidden flex flex-col gap-2">
                      {results.map((a) => (
                        <AgentPickerRow
                          key={a.id}
                          agent={a}
                          selected={selectedIds.has(a.id)}
                          onToggle={toggleAgent}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* A0-2 exclusivity rule — stated before sending. Invite freely;
                  the first to accept wins and the rest close automatically. Full
                  width, wraps naturally at 375 (no fixed height). */}
              <div className="flex items-start gap-2.5 rounded-lg bg-[#F8FAFC] border border-[#E5E7EB] px-3.5 py-3">
                <Users className="w-4 h-4 text-[#6B7280] flex-none mt-0.5" aria-hidden="true" />
                <span className="text-[12px] text-[#4B5563] leading-snug">
                  Invite as many as you like — the first to accept gets the job, and
                  the other invitations close automatically. Only one agent ever
                  searches for a request.
                </span>
              </div>

              {/* A0-2 — one optional personal note that travels to every invited
                  agent (quoted in B8 + email 06). Separate from the spec free-text
                  and the terms note. Full width; textarea wraps/grows, no overflow
                  at 375. */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[12.5px] font-semibold text-[#374151]">
                  Personal note to the agents{" "}
                  <span className="font-normal text-[#9CA3AF]">(optional)</span>
                </label>
                <textarea
                  value={personalNote}
                  onChange={(e) => setPersonalNote(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="Optional personal note to the agents (one note, goes to all)"
                  className={`${inputCls} resize-y min-h-[64px] leading-[1.5]`}
                />
              </div>

              {/* Publication clarity again at the point of sending (DoD 23). */}
              <PublicationNotice variant="n3-create" />
              {error && <p className="text-[12px] text-red-500">{error}</p>}
            </div>
          )}

          {/* Desktop footer — board N3-5a/N3-5b: #F8FAFC bar; step 1 right-
              aligned "Continue" (Cancel kept from the old surface — no artboard
              placement, flagged OPEN), step 2 "Back" / "Send to N agents". */}
          <div className="hidden md:flex items-center justify-between border-t border-[#E5E7EB] bg-[#F8FAFC] px-7 py-3.5 rounded-b-xl">
            {step === 1 ? (
              <>
                <button
                  onClick={() => navigate("/network/trusted-agent")}
                  className="px-4 py-2.5 text-[13px] font-semibold text-gray-500 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setStep(2)}
                  disabled={!step1Valid}
                  className="px-5 py-2.5 bg-[#2283FF] text-white rounded-lg text-[13px] font-semibold hover:bg-[#1B6FE0] disabled:opacity-50"
                >
                  Continue
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2.5 bg-white border border-[#E5E7EB] rounded-lg text-[13px] font-semibold hover:bg-[#F3F4F6]"
                >
                  Back
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending || selected.length === 0}
                  className="px-5 py-2.5 bg-[#2283FF] text-white rounded-lg text-[13px] font-semibold hover:bg-[#1B6FE0] disabled:opacity-50"
                >
                  {sending
                    ? "Sending…"
                    : `Send to ${selected.length} agent${selected.length === 1 ? "" : "s"}`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile sticky bottom action bar — boards M5/M6: full-width primary CTA
          in thumb reach ("Continue — choose agents" / "Send to N agents"). Same
          handlers as the desktop footer buttons. */}
      <div className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-[#E5E7EB] px-4 pt-3 pb-4 z-20">
        {step === 1 ? (
          <button
            onClick={() => setStep(2)}
            disabled={!step1Valid}
            className="w-full bg-[#2283FF] text-white rounded-xl text-[15px] font-semibold py-[15px] hover:bg-[#1B6FE0] disabled:opacity-50"
          >
            Continue: choose agents
          </button>
        ) : (
          <button
            onClick={handleSend}
            disabled={sending || selected.length === 0}
            className="w-full bg-[#2283FF] text-white rounded-xl text-[15px] font-semibold py-[15px] hover:bg-[#1B6FE0] disabled:opacity-50"
          >
            {sending
              ? "Sending…"
              : `Send to ${selected.length} agent${selected.length === 1 ? "" : "s"}`}
          </button>
        )}
      </div>
    </div>
  );
};

export default CreateSearchRequest;
